import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { CLASS_LABEL } from "./Absenteeism";

const PERIODS = [
  { value: "30",  label: "30 dias" },
  { value: "90",  label: "90 dias" },
  { value: "180", label: "180 dias" },
  { value: "365", label: "1 ano" },
];

function fmtDate(daysAgo) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}
const today = () => new Date().toISOString().slice(0, 10);

export default function Report() {
  const [searchParams] = useSearchParams();
  const initialCompany = searchParams.get("empresa") || "";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(initialCompany);
  const [period, setPeriod] = useState("90");
  const [hideCid, setHideCid] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      if (!companyId && r.data.length) setCompanyId(r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = { company_id: companyId, hide_cid: hideCid };
      params.start = fmtDate(parseInt(period, 10));
      params.end = today();
      const r = await api.get("/absenteeism/report-data", { params });
      setData(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [companyId, period, hideCid]);  // eslint-disable-line

  return (
    <div className="space-y-6" data-testid="abs-report-page">
      <header className="no-print">
        <div className="overline">Absenteísmo</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">Relatório PDF</h1>
        <Link to="/app/absenteismo" className="text-sm text-emerald-800 hover:underline mt-2 inline-block">
          <i className="fa-solid fa-arrow-left mr-2"></i>Voltar ao painel
        </Link>
      </header>

      <div className="card no-print flex flex-wrap items-end gap-3">
        <label className="flex-1 min-w-[200px]">
          <div className="overline mb-1">Empresa</div>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="w-full border border-slate-300 px-3 py-2">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.razao_social || c.nome_fantasia}</option>)}
          </select>
        </label>
        <label>
          <div className="overline mb-1">Período</div>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="border border-slate-300 px-3 py-2">
            {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="hide-cid-toggle-label">
          <input type="checkbox" checked={hideCid} onChange={(e) => setHideCid(e.target.checked)}
                 data-testid="hide-cid-toggle" />
          <span className="text-sm font-bold uppercase tracking-wider text-slate-700">Ocultar CIDs</span>
        </label>
        <button onClick={() => window.print()} className="btn btn-primary ml-auto" data-testid="print-btn">
          <i className="fa-solid fa-print"></i> Imprimir / Salvar PDF
        </button>
      </div>

      <div className="card border-l-4 border-amber-400 bg-amber-50/40 no-print">
        <p className="text-sm text-amber-900">
          <i className="fa-solid fa-shield-halved mr-2"></i>
          <b>LGPD — Dados sensíveis de saúde.</b> Marque &ldquo;Ocultar CIDs&rdquo; para gerar a versão
          administrativa (sem códigos CID), apropriada para circulação fora da equipe técnica/médica.
        </p>
      </div>

      {loading && <div className="card text-center text-slate-500">Carregando…</div>}
      {data && !loading && <ReportContent d={data} />}
    </div>
  );
}

function ReportContent({ d }) {
  const dash = d.dashboard;
  const k = dash.kpis;
  return (
    <div className="space-y-6 print:space-y-2">
      {/* COVER */}
      <div className="card avoid-break border-2 border-emerald-900 print:bg-emerald-900 print:text-white">
        <div className="text-center py-8">
          <div className="overline mb-2">Sollute Ocupacional · NR-01</div>
          <h1 className="font-display text-4xl font-extrabold mb-2 print:text-white">
            Relatório de Absenteísmo
          </h1>
          <p className="text-lg italic mb-6 print:text-emerald-50">Inteligência preventiva NR-01</p>
          <div className="text-base">
            <div className="font-bold">{d.company.razao_social}</div>
            {d.company.cnpj && <div className="text-sm">CNPJ {d.company.cnpj}</div>}
          </div>
          <div className="mt-6 text-sm">
            Período: <b>{d.period.start || "—"}</b> a <b>{d.period.end || "—"}</b>
          </div>
          <div className="text-xs mt-2 print:text-emerald-100">
            Gerado em {d.generated_at?.slice(0,10)} por {d.generated_by?.name || d.generated_by?.email}
          </div>
          {d.hide_cid && (
            <div className="mt-4 inline-block px-3 py-1 bg-amber-200 text-amber-900 text-xs font-bold uppercase tracking-wider print:bg-amber-100">
              Versão administrativa — CIDs ocultados (LGPD)
            </div>
          )}
        </div>
      </div>

      <h2 className="font-display text-2xl font-extrabold border-b-2 border-emerald-900 pb-1 page-break-before">1. Indicadores gerais</h2>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 avoid-break">
        {[
          ["Total atestados", k.total_certificates],
          ["Dias perdidos", k.total_days_lost],
          ["Colaboradores afetados", k.employees_affected],
          ["Ativos", k.active_employees],
          ["Taxa absenteísmo", `${k.absenteeism_rate_pct}%`],
        ].map(([label, value]) => (
          <div key={label} className="border-2 border-emerald-900 p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-900">{label}</div>
            <div className="font-display text-2xl font-extrabold mt-1">{value}</div>
          </div>
        ))}
      </div>

      <h2 className="font-display text-2xl font-extrabold border-b-2 border-emerald-900 pb-1">2. Concentração por setor</h2>
      <RankTable items={dash.by_setor} />

      <h2 className="font-display text-2xl font-extrabold border-b-2 border-emerald-900 pb-1">3. Concentração por GHE</h2>
      <RankTable items={dash.by_ghe} />

      <h2 className="font-display text-2xl font-extrabold border-b-2 border-emerald-900 pb-1">
        4. Ranking de {d.hide_cid ? "diagnósticos (agrupados)" : "CIDs"}
      </h2>
      <table className="w-full text-sm border border-slate-300 avoid-break">
        <thead>
          <tr className="bg-emerald-900 text-white">
            <th className="text-left py-1 px-2">{d.hide_cid ? "Diagnóstico" : "CID"}</th>
            <th className="text-left px-2">Grupo</th>
            <th className="text-right px-2">Casos</th>
            <th className="text-right px-2">Dias</th>
          </tr>
        </thead>
        <tbody>
          {(dash.top_cids || []).map((c) => (
            <tr key={c.cid} className="border-b border-slate-200">
              <td className="py-1 px-2 font-mono">{c.cid}</td>
              <td className="px-2">{c.group}</td>
              <td className="text-right px-2 font-bold">{c.count}</td>
              <td className="text-right px-2">{c.days}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 className="font-display text-2xl font-extrabold border-b-2 border-emerald-900 pb-1">5. Tipos de afastamento</h2>
      <RankTable items={(dash.by_classification || []).map((x) => ({ ...x, label: CLASS_LABEL[x.label] || x.label }))} />

      <h2 className="font-display text-2xl font-extrabold border-b-2 border-emerald-900 pb-1">6. Alertas automáticos</h2>
      {d.alerts.alerts.length === 0 ? (
        <p className="text-sm text-slate-600">Nenhum alerta no período.</p>
      ) : (
        <div className="space-y-2">
          {d.alerts.alerts.map((a, i) => (
            <div key={i} className={`border-l-4 p-2 ${
              a.severity === "high" ? "border-rose-700 bg-rose-50"
              : a.severity === "medium" ? "border-amber-600 bg-amber-50"
              : "border-slate-400 bg-slate-50"}`}>
              <div className="font-bold text-sm">{a.title}</div>
              <div className="text-xs">{a.detail}</div>
            </div>
          ))}
        </div>
      )}

      <h2 className="font-display text-2xl font-extrabold border-b-2 border-emerald-900 pb-1">7. Correlação Absenteísmo × NR-01</h2>
      {d.correlation.rows.length === 0 ? (
        <p className="text-sm text-slate-600">Sem dados cruzados para o período.</p>
      ) : (
        <table className="w-full text-sm border border-slate-300 avoid-break">
          <thead>
            <tr className="bg-emerald-900 text-white">
              <th className="text-left py-1 px-2">GHE</th>
              <th className="text-right px-2">Dias afast.</th>
              <th className="text-right px-2">CID F</th>
              <th className="text-right px-2">CID M</th>
              <th className="text-right px-2">Score NR-01</th>
              <th className="px-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {d.correlation.rows.map((r) => (
              <tr key={r.ghe} className={`border-b border-slate-200 ${r.critical ? "bg-rose-50" : ""}`}>
                <td className="py-1 px-2 font-medium">{r.ghe}</td>
                <td className="text-right px-2 font-bold">{r.abs_days}</td>
                <td className="text-right px-2">{r.cid_f}</td>
                <td className="text-right px-2">{r.cid_m}</td>
                <td className="text-right px-2">{r.nr01_score || "—"}</td>
                <td className="px-2 text-xs">{r.critical ? "⚠ CRÍTICO" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2 className="font-display text-2xl font-extrabold border-b-2 border-emerald-900 pb-1">8. Plano de ação</h2>
      {d.action_plans.length === 0 ? (
        <p className="text-sm text-slate-600">Nenhuma ação cadastrada. Gere pela aba <i>Inteligência IA</i>.</p>
      ) : (
        <div className="space-y-2">
          {d.action_plans.map((a) => (
            <div key={a.id} className="border border-slate-300 p-3 avoid-break">
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <div className="font-bold text-sm">{a.problema}</div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-slate-200">{a.prioridade}</span>
              </div>
              <div className="text-xs text-slate-600 mb-1"><b>Evidência:</b> {a.evidencia || "—"}</div>
              <div className="text-xs text-slate-600 mb-1"><b>NR-01:</b> {a.relacao_nr01 || "—"}</div>
              <div className="text-sm mt-1"><b>Ação:</b> {a.acao_recomendada}</div>
              <div className="text-xs text-slate-500 mt-1">
                Responsável: {a.responsavel || "—"} · Prazo: {a.prazo || "—"} · Status: {a.status}
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 className="font-display text-2xl font-extrabold border-b-2 border-emerald-900 pb-1">9. Conclusão técnica</h2>
      <p className="text-sm leading-relaxed">
        Este relatório consolida os indicadores de absenteísmo do período analisado, cruzando-os
        com os fatores psicossociais da NR-01 e indicadores ergonômicos. As ações sugeridas
        devem ser priorizadas conforme a classificação atribuída e revisadas trimestralmente.
        {d.hide_cid && (
          <span className="block mt-2 italic">
            Esta é a versão administrativa — códigos CID foram agrupados em categorias amplas
            para preservar a privacidade dos colaboradores conforme LGPD.
          </span>
        )}
      </p>

      <div className="text-xs text-slate-500 text-center pt-4 border-t border-slate-300 mt-6">
        Relatório gerado pelo Absenteísmo Inteligente NR-01 da Sollute Ocupacional.
      </div>
    </div>
  );
}

function RankTable({ items }) {
  if (!items?.length) return <p className="text-sm text-slate-600">Sem dados.</p>;
  return (
    <table className="w-full text-sm border border-slate-300 avoid-break">
      <thead>
        <tr className="bg-emerald-900 text-white">
          <th className="text-left py-1 px-2">Item</th>
          <th className="text-right px-2">Casos</th>
          <th className="text-right px-2">Dias</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it, i) => (
          <tr key={i} className="border-b border-slate-200">
            <td className="py-1 px-2">{it.label}</td>
            <td className="text-right px-2 font-bold">{it.count}</td>
            <td className="text-right px-2">{it.days}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
